<template>
    <div class="relative">
        <div class="mb-6 flex items-center justify-between">
            <div class="space-y-1">
                <ULink to="/customers" class="flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700">
                    <span>Customers</span>
                    <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                </ULink>
                <div v-if="customer" class="flex items-center space-x-4">
                    <h1 class="text-3xl font-semibold text-gray-700">{{ customerName }}</h1>
                    <FamilyMemberRoleBadge
                        v-if="customerFamilyMember?.role === 'child'"
                        :role="customerFamilyMember.role"
                        color="blue"
                    />
                </div>
            </div>

            <div class="flex items-center gap-x-2">
                <div v-if="auth.hasRole(UserRole.admin, UserRole.medicalDirector, UserRole.doctor)" class="flex items-center space-x-2">
                    <UDropdown :items="actions">
                        <UButton color="black" label="Edit" icon="i-ph-pencil-bold" />
                    </UDropdown>
                </div>
    
                <UButton
                    v-if="auth.hasRole(UserRole.admin)"
                    @click="overlays.deleteCustomer = true"
                    color="red" 
                    label="Delete" 
                    icon="i-ph-trash-bold"
                />
            </div>
        </div>

        <div v-if="customer" class="relative flex items-start gap-4">
            <div class="sticky top-0 flex-shrink-0 w-96 space-y-4">
                <div class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="absolute top-3 right-3 flex-shrink-0 ring-8 ring-white">
                        <Avatar
                            modal
                            size="3xl"
                            :path="customer.avatar" 
                            :placeholder="[customer.first_name, customer.last_name].filter(Boolean).join(' ')" 
                        />
                    </div>
                    
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Details</h3>
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <Field label="ID">
                            <span class="font-mono">{{ customerId }}</span>
                        </Field>
                        <Field label="Full Name">
                            <span>{{ customerName }}</span>
                        </Field>
                        <Field label="Created">
                            <span>{{ dayjs(customer.updated_at).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                        <Field label="Activated">
                            <span v-if="activatedDate">{{ dayjs(activatedDate).format('MMM D, YYYY HH:mm') }}</span>
                        </Field>
                        <Field label="Date of Birth">
                            <span v-if="customer.birth_date">{{ dayjs(customer.birth_date).format('MMM D, YYYY') }} ({{ age(customer.birth_date) }})</span>
                        </Field>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Gender">
                                <span v-if="customer.gender" class="capitalize">{{ customer.gender }}</span>
                            </Field>
                            <Field label="Blood Type">
                                <span v-if="customer.blood_type">{{ customer.blood_type }}</span>
                            </Field>
                        </div>
                        <Field label="Policy Holder">
                            <span v-if="customer?.policy_holder">{{ customer.policy_holder }}</span>
                        </Field>
                        <Field label="Tags">
                            <div v-if="customer.tags?.length" class="flex flex-wrap gap-1 mt-1">
                                <UBadge v-for="tag in customer.tags" :key="tag" :label="tag" color="gray" />
                            </div>
                        </Field>
                        <Field label="Non-member switches">
                            <div v-if="switchedCallCount" class="flex items-center gap-2">
                                <UBadge :label="String(switchedCallCount)" :color="switchedCallCount > 1 ? 'red' : 'orange'" variant="subtle" />
                                <UButton variant="link" color="gray" size="xs" :padded="false" @click="overlays.switchedCalls = true">
                                    View calls
                                </UButton>
                            </div>
                        </Field>
                    </div>
                </div>

                <div v-if="customerFamilyMember" class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center justify-between px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Family</h3>
                        <ULink :to="`/families/${customerFamilyMember.family_id}`" class="flex items-center space-x-1 text-xs font-medium hover:underline">
                            <span>View family</span>
                            <UIcon name="i-ph-arrow-right-bold" />
                        </ULink>
                    </div>

                    <div class="px-6 py-5 space-y-4">
                        <Field label="Name">
                            <span>{{ customerFamilyMember.family?.name || '–' }}</span>
                        </Field>

                        <Field label="Members">
                            <span>{{ customerFamilyMember.family?.members?.length || 0 }}</span>
                        </Field>

                        <Field label="Role">
                            <FamilyMemberRoleBadge :role="customerFamilyMember.role" />
                        </Field>
                    </div>
                </div>

                <div class="relative bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Contact</h3>
                    </div>

                    <div class="px-6 py-5 space-y-4">
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Emergency Phone">
                                <PhoneLabel v-if="customer.metadata?.emergency_phone" :value="customer.metadata.emergency_phone" />
                            </Field>
                            <Field label="Emergency Contact" truncate>
                                <span v-if="customer.metadata?.emergency_contact">{{ customer.metadata.emergency_contact }}</span>
                            </Field>
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Registered Phone">
                                <PhoneLabel v-if="customer.phone" :value="customer.phone" />
                            </Field>
                            <Field label="Alternate Phone">
                                <PhoneLabel v-if="customer.metadata?.alternate_phone" :value="customer.metadata.alternate_phone" />
                            </Field>
                        </div>
                        <Field label="Registered Email">
                            <span v-if="customer.email">{{ customer.email }}</span>
                        </Field>
                    </div>
                </div>
            </div>
            <div class="flex-1 min-w-0">
                <div class="flex flex-col bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
                    <UTabs 
                        v-model="selectedTab" 
                        class="flex-shrink-0"
                        :items="tabs" 
                        :ui="tabsUi"
                    >
                        <template #default="{ item }">
                            <div class="flex items-center space-x-2">
                                <UIcon :name="item.icon" class="w-4 h-4 flex-shrink-0" />
                                <span>{{ item.label }}</span>
                            </div>
                        </template>
                    </UTabs>

                    <div class="flex-1 p-5">
                        <template v-if="currentTab === 'reports'">
                            <div v-if="!customer.reports?.length" class="h-96 flex flex-col space-y-1 items-center justify-center p-4 pt-6">
                                <UIcon name="i-ph-file-x-duotone" class="size-5 text-gray-500" />
                                <span class="text-gray-500 text-[13px] italic">No reports.</span>
                            </div>
                            <div v-else class="space-y-4">
                                <CallReportCard 
                                    v-for="report in customer.reports" 
                                    :key="report.id" 
                                    :report="report"
                                    :subscription="latestSubscription"
                                    @refresh="fetchData(true, true)"
                                />
                            </div>
                        </template>
                        <template v-else-if="currentTab === 'medical'">
                            <div class="space-y-6">
                                <div class="space-y-1">
                                    <h4 class="text-sm font-medium text-gray-600">Chronic Diseases</h4>
                                    <div class="px-5 py-4 rounded-md border text-sm">
                                        <p v-if="customer.chronic_diseases">{{ customer.chronic_diseases }}</p>
                                        <span v-else class="text-gray-300">No data</span>
                                    </div>
                                </div>
                                <div class="space-y-1">
                                    <h4 class="text-sm font-medium text-gray-600">Chronic Medication</h4>
                                    <div class="px-5 py-4 rounded-md border text-sm">
                                        <p v-if="customer.chronic_medication">{{ customer.chronic_medication }}</p>
                                        <span v-else class="text-gray-300">No data</span>
                                    </div>
                                </div>
                                <div class="space-y-1">
                                    <h4 class="text-sm font-medium text-gray-600">Surgeries</h4>
                                    <div class="px-5 py-4 rounded-md border text-sm">
                                        <p v-if="customer.surgeries">{{ customer.surgeries }}</p>
                                        <span v-else class="text-gray-300">No data</span>
                                    </div>
                                </div>
                                <div class="space-y-1">
                                    <h4 class="text-sm font-medium text-gray-600">Allergies</h4>
                                    <div class="px-5 py-4 rounded-md border text-sm">
                                        <p v-if="customer.allergies">{{ customer.allergies }}</p>
                                        <span v-else class="text-gray-300">No data</span>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else-if="currentTab === 'subscriptions'">
                            <CustomerSubscriptionsPanel
                                :current-subscription="currentSubscription"
                                :subscriptions-history="subscriptionsHistory"
                                @create="overlays.createSubscription = true"
                                @extend="overlays.extendSubscription = true"
                                @cancel="overlays.cancelSubscription = true"
                            />
                        </template>
                        <template v-else-if="currentTab === 'transports'">
                            <div v-if="!transports.length" class="h-96 flex flex-col space-y-1 items-center justify-center p-4 pt-6">
                                <UIcon name="i-ph-ambulance-duotone" class="size-5 text-gray-500" />
                                <span class="text-gray-500 text-[13px] italic">No transports.</span>
                            </div>
                            <UTable
                                v-else
                                :rows="transports"
                                :columns="transportColumns"
                                :ui="{ td: { padding: 'py-3' } }"
                            >
                                <template #number-data="{ row }">
                                    <ULink :to="`/transports/${row.id}`" class="font-mono hover:underline">{{ row.number.toString().padStart(6, '0') }}</ULink>
                                </template>
                                <template #provider-data="{ row }">
                                    <div v-if="row.provider" class="flex items-center space-x-2.5">
                                        <Avatar bucket="media" folder="providers" size="sm" :placeholder="row.provider.name" :path="row.provider.avatar" />
                                        <span>{{ row.provider.name }}</span>
                                    </div>
                                </template>
                                <template #route-data="{ row }">
                                    <div class="leading-5">
                                        <p>{{ row.departure_address?.city || row.departure_address?.formatted?.split(',')[0] || '–' }} → {{ row.arrival_address?.city || row.arrival_address?.formatted?.split(',')[0] || '–' }}</p>
                                        <span class="text-sm text-gray-500">{{ row.trip_type === 'round_trip' ? 'Round Trip' : 'One Way' }}</span>
                                    </div>
                                </template>
                                <template #scheduled-data="{ row }">
                                    <div class="leading-5">
                                        <p>{{ dayjs(row.scheduled_date).format('MMM D, YYYY') }}</p>
                                        <span class="text-sm text-gray-500">{{ row.scheduled_time?.slice(0, 5) }}</span>
                                    </div>
                                </template>
                                <template #created_by-data="{ row }">
                                    <span v-if="row.creator">{{ row.creator.name }}</span>
                                    <span v-else class="text-gray-400">–</span>
                                </template>
                                <template #status-data="{ row }">
                                    <BadgeTransportStatus :status="row.status" />
                                </template>
                                <template #actions-data="{ row }">
                                    <UButton
                                        color="gray"
                                        variant="outline"
                                        label="View"
                                        size="xs"
                                        @click="navigateTo(`/transports/${row.id}`)"
                                    />
                                </template>
                            </UTable>
                        </template>
                        <template v-else-if="currentTab === 'interventions'">
                            <div v-if="!interventions.length" class="h-96 flex flex-col space-y-1 items-center justify-center p-4 pt-6">
                                <UIcon name="i-ph-first-aid-duotone" class="size-5 text-gray-500" />
                                <span class="text-gray-500 text-[13px] italic">No interventions.</span>
                            </div>
                            <UTable
                                v-else
                                :rows="interventions"
                                :columns="interventionColumns"
                                :ui="{ td: { padding: 'py-3' } }"
                            >
                                <template #number-data="{ row }">
                                    <ULink :to="`/field-posts/${row.post_id}/interventions/${row.id}`" class="font-mono hover:underline">{{ row.number.toString().padStart(6, '0') }}</ULink>
                                </template>
                                <template #post-data="{ row }">
                                    <span v-if="row.post">{{ row.post.name }}</span>
                                    <span v-else class="text-gray-400">–</span>
                                </template>
                                <template #reason-data="{ row }">
                                    <div class="leading-5">
                                        <p v-if="row.reason">{{ row.reason.label }}</p>
                                        <p v-else class="text-gray-400">–</p>
                                        <span v-if="row.disposition" class="text-sm text-gray-500">{{ row.disposition.label }}</span>
                                    </div>
                                </template>
                                <template #arrived-data="{ row }">
                                    <div class="leading-5">
                                        <p>{{ dayjs(row.arrived_at).format('MMM D, YYYY HH:mm') }}</p>
                                        <span v-if="row.discharged_at" class="text-sm text-gray-500">Stayed {{ Math.max(0, dayjs(row.discharged_at).diff(dayjs(row.arrived_at), 'minute')) }} min</span>
                                    </div>
                                </template>
                                <template #status-data="{ row }">
                                    <BadgeInterventionStatus :status="row.status" />
                                </template>
                                <template #actions-data="{ row }">
                                    <UButton
                                        color="gray"
                                        variant="outline"
                                        label="View"
                                        size="xs"
                                        @click="navigateTo(`/field-posts/${row.post_id}/interventions/${row.id}`)"
                                    />
                                </template>
                            </UTable>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <OverlayCustomerEditProfileModal v-model:open="overlays.editProfile" @reload="fetchData" :customer="customer" />
        <OverlayCustomerEditContactModal v-model:open="overlays.editContact" @reload="fetchData" :customer="customer" />
        <OverlayCustomerEditMedicalHistoryModal v-model:open="overlays.editMedicalHistory" @reload="fetchData" :customer="customer" />
        <OverlayCustomerDeleteCustomerModal v-model:open="overlays.deleteCustomer" @confirmed="navigateTo('/customers')" :customer="customer" />
        <OverlayCustomerCreateSubscriptionModal v-model:open="overlays.createSubscription" @reload="fetchData(true)" :customer="customer" />
        <OverlayCustomerExtendSubscriptionModal v-model:open="overlays.extendSubscription" @reload="fetchData(true)" :subscription="currentSubscription" />
        <OverlayCustomerCancelSubscriptionModal v-model:open="overlays.cancelSubscription" @reload="fetchData(true)" :subscription="currentSubscription" />
        <OverlayCustomerSwitchedCallsModal v-model:open="overlays.switchedCalls" :customer-id="customer?.id" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import CallReportCard from '~/components/elements/CallReportCard.vue'
import CustomerSubscriptionsPanel from '~/components/elements/CustomerSubscriptionsPanel.vue'
import type { Customer } from '~/types/customer'
import type { Transport } from '~/types/transport'
import type { Intervention } from '~/types/fieldPost'
import { SubscriptionSourceType } from '~/types/subscription'
import { UserRole } from '~/types/user'

const route = useRoute()
const auth = useAuthStore()

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.medicalDirector, UserRole.doctor, UserRole.operator] })
useHead({ title: 'View Customer' })

const client = useSupabaseClient()

const { age } = useNumber()

const loading = ref(false)
const selectedTab = ref(0)
const customer = ref<Customer>()
const transports = ref<Transport[]>([])
const interventions = ref<Intervention[]>([])
const switchedCallCount = ref(0)

const overlays = reactive({
    editProfile: false,
    editContact: false,
    editMedicalHistory: false,
    deleteCustomer: false,
    createSubscription: false,
    extendSubscription: false,
    cancelSubscription: false,
    switchedCalls: false,
})

const actions = computed(() => {
    return [[
        { label: 'Profile', icon: 'i-ph-user-circle-duotone', click: () => overlays.editProfile = true, hidden: !auth.hasRole(UserRole.admin, UserRole.medicalDirector) },
        { label: 'Registered Contact', icon: 'i-ph-identification-badge-duotone', click: () => overlays.editContact = true, hidden: !auth.hasRole(UserRole.admin, UserRole.medicalDirector) },
        { label: 'Medical History', icon: 'i-ph-first-aid-kit-duotone', click: () => overlays.editMedicalHistory = true },
    ].filter((a) => !a.hidden)]
})

const tabs = computed(() => [
    { key: 'reports', label: 'Call Reports', icon: 'i-ph-file-text-duotone' },
    { key: 'transports', label: 'Transports', icon: 'i-ph-ambulance-duotone' },
    { key: 'interventions', label: 'Interventions', icon: 'i-ph-first-aid-duotone', hidden: !auth.isEnabled('field_posts') },
    { key: 'medical', label: 'Medical History', icon: 'i-ph-first-aid-kit-duotone' },
    { key: 'subscriptions', label: 'Subscriptions', icon: 'i-ph-shield-plus-duotone' },
].filter((t) => !t.hidden))

const currentTab = computed(() => tabs.value[selectedTab.value]?.key)

const transportColumns = [
    { key: 'number', label: '#', class: 'w-px' },
    { key: 'provider', label: 'Provider' },
    { key: 'route', label: 'Route' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'created_by', label: 'Created By' },
    { key: 'status', label: 'Status', class: 'w-px' },
    { key: 'actions', class: 'w-px' },
]

const interventionColumns = [
    { key: 'number', label: '#', class: 'w-px' },
    { key: 'post', label: 'Post' },
    { key: 'reason', label: 'Reason' },
    { key: 'arrived', label: 'Arrived' },
    { key: 'status', label: 'Status', class: 'w-px' },
    { key: 'actions', class: 'w-px' },
]

const tabsUi = {
    wrapper: 'space-y-0 pt-1',
    container: 'border-b border-gray-300',
    list: {
        base: '!flex space-x-4 w-full overflow-x-auto px-1',
        background: 'bg-transparent',
        rounded: 'rounded-none',
        padding: 'p-0',
        height: 'h-auto',
        marker: {
            base: 'w-auto',
            wrapper: 'border-b-2 border-primary-600',
            background: 'bg-transparent',
            rounded: 'rounded-none',
            shadow: 'shadow-none',
        },
        tab: {
            base: 'h-auto w-auto',
            padding: 'px-3 py-3.5',
        },
    },
}

const customerId = computed(() => {
    if (customer.value) {
        return customer.value.number.toString().padStart(8, '0')
    }
})

const customerName = computed(() => {
    if (customer.value) {
        return [customer.value.first_name, customer.value.middle_name, customer.value.last_name].filter(Boolean).join(' ')
    }
})

const customerFamilyMember = computed(() => {
    return customer.value?.family_members
})

const activatedDate = computed(() => {
    if (customer.value?.subscriptions) {
        return customer.value?.subscriptions.findLast(() => true)?.created_at
    }
})

const currentSubscription = computed(() => {
    return customer.value?.subscriptions?.find((subscription) => {
        const expiresInDays = dayjs(subscription.expires_at).diff(dayjs(), 'days')
        return subscription.id === customer.value?.subscription_id && expiresInDays >= 0
    })
})

const latestSubscription = computed(() => {
    if (customer.value?.subscription_id) {
        return customer.value?.subscriptions?.find((s) => s.id === customer.value?.subscription_id)
    }
})

const subscriptionsHistory = computed(() => {
    return customer.value?.subscriptions?.filter((subscription) => {
        return subscription.id !== currentSubscription.value?.id
    })
})

onMounted(() => {
    fetchData()
    fetchTransports()
    fetchInterventions()
    fetchSwitchedCallCount()
})

async function fetchData(silent?: boolean, withoutSubscriptions?: boolean) {
    if (!silent) {
        loading.value = true
    }

    const { data, error } = await client
        .from('customers')
        .select(`
            *,
            family_members!family_members_customer_id_fkey (id, role, status, family_id, family:families (id, name, members:family_members (id))),
            subscriptions (
                id,
                partner_id,
                invoice_id,
                source_id,
                source_type,
                is_active,
                created_at,
                expires_at,
                ends_at,
                partner:partners (id, name, avatar, metadata)
            ),
            reports:call_reports (
                *,
                user:users (id, name, role),
                customer:customers (id, phone, policy_holder),
                call:calls (id, number, source, status),
                call_reason:options!call_reports_call_reason_id_fkey (id, label, description),
                call_decision:options!call_reports_call_decision_id_fkey (id, label, description),
                diagnosis:options!call_reports_diagnosis_id_fkey (id, label, description, metadata)
            )
        `)
        .eq('id', route.params.id)
        .order('number', { ascending: false, referencedTable: 'reports' })
        .order('expires_at', { ascending: false, referencedTable: 'subscriptions' })
        .returns<Customer>()
        .single()

    if (data) {
        customer.value = data as Customer

        if (!withoutSubscriptions) {
            customer.value.subscriptions?.forEach(async (subscription) => {
                if (subscription.source_id) {
                    if (subscription.source_type === SubscriptionSourceType.coupon) {
                        const { data } = await client.from('coupons')
                            .select('id, code')
                            .eq('id', subscription.source_id)
                            .single()

                        if (data) {
                            subscription.source_values = [data.code]
                        }
                    } 
                    else if (subscription.source_type === SubscriptionSourceType.credential) {
                        const { data } = await client.rpc('get_subscription_source_credentials', { p_subscription_ids: [subscription.id] } as never)
                            .single<{ fields: Record<string, string> }>()

                        if (data) {
                            subscription.source_values = Object.keys(data.fields).map((key) => {
                                return `${key} | ${data.fields[key]}`
                            })
                        }
                    }
                }
            })
        }
    } else if (error) {
        console.error(error)
    }

    loading.value = false
}

async function fetchTransports() {
    const { data } = await client
        .from('transports')
        .select(`
            id, number, status, trip_type, departure_address, arrival_address, scheduled_date, scheduled_time,
            provider:providers (id, name, avatar),
            creator:users (id, name)
        `)
        .eq('customer_id', route.params.id)
        .order('created_at', { ascending: false })

    if (data) {
        transports.value = data as any
    }
}

async function fetchInterventions() {
    if (!auth.isEnabled('field_posts')) return

    const { data } = await client
        .from('interventions')
        .select(`
            id, number, post_id, status, arrived_at, discharged_at,
            post:field_posts (id, name),
            reason:options!interventions_reason_id_fkey (id, label),
            disposition:options!interventions_disposition_id_fkey (id, label)
        `)
        .eq('customer_id', route.params.id)
        .order('arrived_at', { ascending: false })

    if (data) {
        interventions.value = data as unknown as Intervention[]
    }
}

async function fetchSwitchedCallCount() {
    const { data } = await client
        .from('membership_switch_counts')
        .select('switched_call_count')
        .eq('customer_id', route.params.id)
        .maybeSingle()

    switchedCallCount.value = (data as unknown as { switched_call_count: number } | null)?.switched_call_count ?? 0
}
</script>
